import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Mail, CheckCircle2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CONSENT_VERSION = 'woty-2026-v1';

/** Email magic-link vote request modal. One vote per verified email. */
const VoteModal = ({ open, onClose, slug, artworkTitle, artistName }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [optIn, setOptIn] = useState(false); // unchecked by default — CASL
  const [state, setState] = useState('form'); // form | sent | already
  const [msg, setMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setEmail(''); setFirstName(''); setOptIn(false);
    setState('form'); setMsg(''); setError(''); setSubmitting(false);
  };
  const close = () => { onClose(); setTimeout(reset, 300); };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Please enter a valid email address.'); return; }
    setError(''); setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contest/vote/request`, {
        slug,
        email: email.trim().toLowerCase(),
        firstName: firstName.trim(),
        marketingOptIn: optIn,
        consentVersion: CONSENT_VERSION,
      });
      setMsg(data.message || '');
      setState(data.status === 'already_voted' ? 'already' : 'sent');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="vote-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {state === 'form' ? 'Vote for this artwork' : 'Almost there!'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Confirm your vote for the 2026 Wine of the Year by entering your email.
          </DialogDescription>
        </DialogHeader>

        {state === 'form' && (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-gray-600">
              You're voting for <strong>{artworkTitle}</strong>{artistName ? <> by {artistName}</> : null}.
              Enter your email and we'll send a one-time link to confirm your vote.
            </p>
            <div>
              <Input
                type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name (optional)" autoComplete="given-name"
                data-testid="vote-firstname-input"
              />
            </div>
            <div>
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com" inputMode="email" autoComplete="email"
                data-testid="vote-email-input"
              />
              {error && <p className="mt-2 text-sm text-red-600" data-testid="vote-error">{error}</p>}
            </div>

            {/* Optional marketing opt-in (unchecked by default, does not affect voting) */}
            <div className="rounded-lg border border-gray-200 bg-[#fdfbf5] p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={optIn}
                  onCheckedChange={(v) => setOptIn(v === true)}
                  className="mt-0.5"
                  data-testid="vote-marketing-optin"
                />
                <span className="text-sm text-gray-700 leading-snug">
                  <span className="font-medium">Keep me in the loop! 🍷</span><br />
                  I'd love to receive occasional emails from <strong>The Lokal Winery</strong> featuring exclusive
                  discounts, upcoming events, new wines, local artist showcases, giveaways, and exciting partnership
                  opportunities.
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-2 pl-7">
                You can unsubscribe anytime. No spam, just the good stuff! See our{' '}
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#722F37' }} data-testid="vote-privacy-link">privacy policy</a>{' '}
                or <a href="mailto:info@thelokal.ca" className="underline" style={{ color: '#722F37' }} data-testid="vote-contact-link">contact us</a>. Sent by The Lokal Winery, Fenelon Falls, ON.
              </p>
            </div>

            <Button type="submit" disabled={submitting} className="w-full" data-testid="vote-submit"
              style={{ background: '#722F37' }}>
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4 mr-2" /> Send my voting link</>}
            </Button>
            <p className="text-xs text-gray-400 text-center">One vote per person. We only use your email to verify your vote — marketing is optional and separate.</p>
          </form>
        )}

        {(state === 'sent' || state === 'already') && (
          <div className="text-center py-4" data-testid="vote-result">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: state === 'sent' ? '#722F37' : '#FDB913' }} />
            <p className="text-gray-700">{msg}</p>
            {state === 'sent' && optIn && (
              <p className="text-sm text-gray-500 mt-2" data-testid="vote-optin-note">
                We'll add you to The Lokal mailing list once your vote is confirmed.
              </p>
            )}
            <Button onClick={close} variant="outline" className="mt-5" data-testid="vote-close">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VoteModal;
